import { useGetPublicProductPlanV2Query } from 'pricing-pages-ui-public-app/__generated__/chirp/com/hubspot/buying/rpc/PublicProductRpc.hooks';
import { PLAN_REQUEST_DEBOUNCE_MS, useDebouncedValueByKey } from 'pricing-pages-ui-components/hooks/useDebouncedValueByKey';
import Raven from 'raven-js';
import { useCallback, useMemo, useState } from 'react';
import { DEFAULT_CHIRP_TIMEOUT } from '../../constants/chirpDefaults';
import { isAbortedQueryError } from '../../utils/isAbortedQueryError';
import { buildFscCheckoutCtas, buildFscOrderDetails, buildFscPlanCart } from './fscOrderSummaryHelpers';
export function useFscOrderSummary({
  catalog,
  selectedTier,
  cart,
  term,
  browserLocale,
  preferredCountryCode,
  currencyCode
}) {
  const liveRequest = useMemo(() => ({
    cart,
    variables: {
      productCart: buildFscPlanCart(cart),
      browserLocale,
      preferredCountryCode,
      currencyCode
    }
  }), [browserLocale, cart, currencyCode, preferredCountryCode]);
  const request = useDebouncedValueByKey(liveRequest, JSON.stringify(liveRequest), PLAN_REQUEST_DEBOUNCE_MS);
  const handleError = useCallback(error => {
    if (isAbortedQueryError(error)) return;
    Raven.captureException(error, {
      fingerprint: ['failed-to-fetch-fsc-configure-order-summary'],
      extra: {
        currencyCode,
        preferredCountryCode,
        selectedTier
      }
    });
  }, [currencyCode, preferredCountryCode, selectedTier]);
  const {
    data,
    loading,
    error
  } = useGetPublicProductPlanV2Query({
    variables: request.variables,
    context: {
      timeout: DEFAULT_CHIRP_TIMEOUT
    },
    onError: handleError
  });
  const isFresh = request === liveRequest && !loading;
  const freshDetails = useMemo(() => {
    if (!isFresh || !data) return null;
    return buildFscOrderDetails({
      catalog,
      tier: selectedTier,
      cart: request.cart,
      productPlanOptions: data.productPlanOptions,
      term,
      currencyCode
    });
  }, [catalog, currencyCode, data, isFresh, request, selectedTier, term]);
  const [lastDetails, setLastDetails] = useState(null);
  if (freshDetails && freshDetails !== lastDetails) {
    setLastDetails(freshDetails);
  }
  const hasFailed = isFresh && (error !== undefined && !isAbortedQueryError(error) || Boolean(data) && !freshDetails);
  const displayedDetails = freshDetails !== null && freshDetails !== void 0 ? freshDetails : isFresh ? null : lastDetails;
  const isRepricing = !freshDetails && displayedDetails !== null;
  const checkoutCtas = useMemo(() => displayedDetails && !hasFailed ? buildFscCheckoutCtas(displayedDetails, isRepricing) : [], [displayedDetails, hasFailed, isRepricing]);
  let content;
  if (hasFailed) {
    content = {
      status: 'error'
    };
  } else if (displayedDetails) {
    content = Object.assign({
      status: 'ready'
    }, displayedDetails.content);
  } else {
    content = {
      status: 'loading',
      previous: null
    };
  }
  return {
    content,
    checkoutCtas
  };
}